import { useEffect, useState } from "react";
import { endpoints } from "../api/endpoints";
import { useApi, useFreshness, useKeyedWrite, useNow } from "../api/hooks";
import { newClientId } from "../api/idempotency";
import type { Alert, AlertStatus, AlertSummary, Channel, LocationInput } from "../api/types";
import { can } from "../auth/roles";
import { FlagBadges, StatusBadge } from "../components/Badges";
import { Dialog } from "../components/Dialog";
import { ALERT_FLAG, ALERT_STATUS, CHANNEL, TYPED_CHANNELS, statusOf } from "../components/labels";
import { Notice, ProblemAlert, QueryState, StaleNotice, UnknownOutcome } from "../components/ProblemAlert";
import { useFireContext } from "../context";
import { formatDateTime, formatSeconds, lusakaLocalToUtc, secondsBetween } from "../format/datetime";
import { AlertDetail } from "./AlertDetail";
import { LocationPicker } from "./LocationPicker";
import { personLabel, ScreenHeader, useConfigNames } from "./shared";
import { Button, StatusDot } from "@lcc/ui";
import { Tabs } from "../components/Tabs";

interface NewAlertForm {
  clientId: string;
  channel: string;
  receivedAt: string;
  callerName: string;
  callerPhone: string;
  location: LocationInput | null;
  placeDescription: string;
  incidentType: string;
  note: string;
}

const blankForm = (): NewAlertForm => ({
  clientId: newClientId(),
  channel: "phone",
  receivedAt: "",
  callerName: "",
  callerPhone: "",
  location: null,
  placeDescription: "",
  incidentType: "",
  note: "",
});

/**
 * Record an alert taken by phone, USSD relay, radio or in person (`createAlert`). The console's `client_id` and the
 * Idempotency-Key belong to this form: after an unknown outcome the whole form is locked and "Try again" resends it
 * unchanged; "Discard and start again" gives a new `client_id` too (the first may have created an alert).
 */
function NewAlertDialog({ onClose, onCreated }: { onClose: () => void; onCreated: (a: Alert) => void }) {
  const names = useConfigNames();
  const write = useKeyedWrite((api, body: Record<string, unknown>, key) => endpoints.createAlert(api, body as never, key), "createAlert:new");
  const kept = write.lockedForm as NewAlertForm | undefined;
  const [f, setF] = useState<NewAlertForm>(() => kept ?? blankForm());
  const [err, setErr] = useState<string | null>(null);
  const set = <K extends keyof NewAlertForm>(k: K, v: NewAlertForm[K]) => setF((x) => ({ ...x, [k]: v }));
  const locked = write.locked;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (locked) return write.mutate({}, { onSuccess: onCreated });
    const body: Record<string, unknown> = { client_id: f.clientId, channel: f.channel };
    if (f.receivedAt) {
      const at = lusakaLocalToUtc(f.receivedAt);
      if (!at) return setErr("Give when the call started as a date and time, or leave it empty for now.");
      body.received_at = at;
    }
    const phone = f.callerPhone.replace(/[\s-]/g, "");
    if (phone && !/^\+[1-9][0-9]{7,14}$/.test(phone)) return setErr("Type the caller's number in international form, e.g. +260971234567.");
    if (f.callerName.trim()) body.caller_name = f.callerName.trim();
    if (phone) body.caller_phone = phone;
    if (f.location) body.location = f.location;
    if (f.placeDescription.trim()) body.place_description = f.placeDescription.trim();
    if (f.incidentType) body.incident_type = f.incidentType;
    if (f.note.trim()) body.note = f.note.trim();
    setErr(null);
    write.remember(f);
    write.mutate(body, { onSuccess: onCreated });
  };

  return (
    <Dialog title="New alert (phone, radio, walk-in or USSD relay)" onClose={onClose} focusContainer={locked}>
      <form className="fer-form" onSubmit={submit} noValidate>
        <fieldset className="fer-bare-fieldset" disabled={locked}>
          <label className="fer-field">
            <span>How it came in (required)</span>
            <select value={f.channel} onChange={(e) => set("channel", e.target.value)}>
              {TYPED_CHANNELS.map((c) => (
                <option key={c} value={c}>
                  {CHANNEL[c].label}
                </option>
              ))}
            </select>
          </label>
          <label className="fer-field">
            <span>When the call started (Lusaka time; empty = now)</span>
            <input type="datetime-local" value={f.receivedAt} onChange={(e) => set("receivedAt", e.target.value)} />
          </label>
          <div className="fer-form-grid">
            <label className="fer-field">
              <span>Caller's name</span>
              <input value={f.callerName} maxLength={200} onChange={(e) => set("callerName", e.target.value)} />
            </label>
            <label className="fer-field">
              <span>Caller's phone</span>
              <input value={f.callerPhone} maxLength={20} inputMode="tel" onChange={(e) => set("callerPhone", e.target.value)} />
            </label>
          </div>
          <label className="fer-field">
            <span>What happened (suggested type)</span>
            <select value={f.incidentType} onChange={(e) => set("incidentType", e.target.value)}>
              <option value="">Not known yet</option>
              {(names.staff?.incident_types ?? []).map((t) => (
                <option key={t.key} value={t.key}>
                  {names.incidentType(t.key)}
                </option>
              ))}
            </select>
          </label>
          <label className="fer-field">
            <span>What the caller said about the place</span>
            <textarea rows={2} maxLength={500} value={f.placeDescription} onChange={(e) => set("placeDescription", e.target.value)} />
          </label>
          <label className="fer-field">
            <span>Note</span>
            <textarea rows={2} maxLength={2000} value={f.note} onChange={(e) => set("note", e.target.value)} />
          </label>
        </fieldset>
        <h4>Location (optional: you can send a location link or set it later)</h4>
        <LocationPicker value={f.location} onChange={(l) => set("location", l)} disabled={locked} />
        {err && (
          <p className="fer-form-error" role="alert">
            {err}
          </p>
        )}
        <ProblemAlert error={write.error} />
        <UnknownOutcome
          write={{
            ...write,
            discard: () => {
              write.discard();
              // The first request may have created an alert under this client_id: a changed form needs a new one.
              setF((x) => ({ ...x, clientId: newClientId() }));
            },
          }}
          retry={() => write.mutate({}, { onSuccess: onCreated })}
        />
        <div className="fer-actions">
          <Button variant="secondary" onClick={onClose}>
            Back
          </Button>
          <Button type="submit" disabled={write.isPending}>
            Record the alert
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function RefusedSosPanel() {
  const { refreshMs } = useFireContext();
  const q = useApi(["refused-sos"], endpoints.listRefusedSos, { refetchInterval: refreshMs });
  const items = q.data ?? [];
  return (
    <section aria-label="SOS refused outside the service area">
      <h3>SOS refused outside the service area ({items.length})</h3>
      <p className="fer-hint">
        The caller was shown the national emergency numbers. Areas are rounded to about 1 km and carry no caller, so nothing
        can be done from here; a cluster at the boundary may mean the service area is wrong.
      </p>
      <QueryState isLoading={q.isLoading} error={q.error} label="refused SOS" />
      {items.length > 0 && (
        <ul className="fer-plain-list">
          {items.map((r) => (
            <li key={r.refused_id}>
              {formatDateTime(r.received_at)} near {r.lon.toFixed(2)}, {r.lat.toFixed(2)}
              {r.incident_type ? ` (${r.incident_type})` : ""}; shown until {formatDateTime(r.visible_until)}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * The live queue beside an open alert (review FW2): how many alerts wait, the oldest wait, any SOS highlighted, and
 * each one a click away, so a new SOS is never out of sight while a call-taker works one alert.
 */
function QueuePanel({ openId, onOpen, onBack }: { openId: string; onOpen: (id: string) => void; onBack: () => void }) {
  const { refreshMs } = useFireContext();
  const now = useNow();
  const q = useApi(["alerts", "new", ""], (api) => endpoints.listAlerts(api, { status: ["new"] }), { refetchInterval: refreshMs, keepPrevious: true });
  const fresh = useFreshness(q);
  const clockNow = fresh.stale && fresh.refreshedAt ? fresh.refreshedAt : now;
  const rows: AlertSummary[] = q.data ?? [];
  const sos = rows.filter((a) => a.channel === "app_sos");
  const oldest = rows[0];
  return (
    <aside className="fer-queue" aria-label="Waiting alerts">
      <h3>Unhandled alerts ({rows.length})</h3>
      {oldest && (
        <p className="fer-clock" role="timer" aria-live="off">
          Oldest waiting {formatSeconds(secondsBetween(oldest.received_at, clockNow))}
          {fresh.stale ? " (stopped: not refreshed)" : ""}
        </p>
      )}
      {sos.length > 0 && <StatusDot tone="emergency" label={`${sos.length} SOS waiting`} />}
      {fresh.stale ? <StaleNotice {...fresh} error={q.error} what="queue" /> : <QueryState isLoading={q.isLoading} error={q.error} label="the queue" />}
      {rows.length > 0 && (
        <ul className="fer-queue-list" aria-label="Alerts waiting">
          {rows.map((a) => (
            <li key={a.alert_id} className="fer-queue-item" data-current={a.alert_id === openId ? "true" : undefined}>
              <span>
                <button
                  type="button"
                  className="fer-link"
                  aria-current={a.alert_id === openId ? "true" : undefined}
                  onClick={() => onOpen(a.alert_id)}
                  aria-label={`Switch to alert ${a.reference}${a.channel === "app_sos" ? " (SOS)" : ""}`}
                >
                  {a.reference}
                </button>
                {a.alert_id === openId ? " (open)" : ""}
                <span className="fer-sub">
                  <StatusBadge status={statusOf(CHANNEL, a.channel)} />
                </span>
              </span>
              <span className="fer-num">{formatSeconds(secondsBetween(a.received_at, clockNow))}</span>
            </li>
          ))}
        </ul>
      )}
      <div className="fer-actions fer-actions--start">
        <Button variant="secondary" size="sm" onClick={onBack}>
          Back to the full queue
        </Button>
      </div>
    </aside>
  );
}

type QueueView = "new" | "handled";

/**
 * The call-taker's intake queue: unhandled alerts, oldest first, each with its live waiting clock, masked caller,
 * flags, duplicate count and who is handling it; opening one shows it in full. New alerts are typed here.
 */
export function IntakeScreen() {
  const { principal, focus, refreshMs } = useFireContext();
  const now = useNow();
  const names = useConfigNames();
  const [view, setView] = useState<QueueView>("new");
  const [channel, setChannel] = useState<Channel | "">("");
  const [open, setOpen] = useState<string | null>(focus.alertId ?? null);
  const [creating, setCreating] = useState(false);
  useEffect(() => {
    if (focus.alertId) setOpen(focus.alertId);
  }, [focus.alertId]);
  const statuses: AlertStatus[] = view === "new" ? ["new"] : ["confirmed", "linked", "dismissed"];
  const q = useApi(["alerts", view, channel], (api) => endpoints.listAlerts(api, { status: statuses, channel: channel ? [channel] : undefined }), {
    refetchInterval: refreshMs,
    keepPrevious: true,
  });
  const fresh = useFreshness(q);
  const clockNow = fresh.stale && fresh.refreshedAt ? fresh.refreshedAt : now;
  const rows: AlertSummary[] = q.data ?? [];

  if (open) {
    return (
      <div className="fer-screen">
        <ScreenHeader title="Intake queue" />
        <div className="fer-split fer-split--queue">
          <QueuePanel openId={open} onOpen={setOpen} onBack={() => setOpen(null)} />
          <div className="fer-detailpane">
            <AlertDetail key={open} alertId={open} onClose={() => setOpen(null)} onOpenAlert={setOpen} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fer-screen">
      <ScreenHeader title="Intake queue">
        {can(principal, "createAlert") && (
          <Button onClick={() => setCreating(true)}>
            New alert
          </Button>
        )}
      </ScreenHeader>
      <Tabs
        label="Show"
        tabs={[
          { key: "new" as const, label: "Unhandled", count: view === "new" && q.data ? rows.length : undefined },
          { key: "handled" as const, label: "Handled (to reopen a mistake)" },
        ]}
        current={view}
        onChange={setView}
      >
        <div className="fer-toolbar">
          <label className="fer-field fer-field--inline">
            <span>Channel</span>
            <select value={channel} onChange={(e) => setChannel(e.target.value as Channel | "")}>
              <option value="">All</option>
              {Object.entries(CHANNEL).map(([k, v]) => (
                <option key={k} value={k}>
                  {v.label}
                </option>
              ))}
            </select>
          </label>
        </div>
        {fresh.stale ? <StaleNotice {...fresh} error={q.error} what="queue" /> : <QueryState isLoading={q.isLoading} error={q.error} label="alerts" />}
        {q.data && rows.length === 0 && <p className="fer-muted">{view === "new" ? "No alert is waiting." : "No handled alert."}</p>}
        {rows.length > 0 && (
          <table className="lcc-table" aria-label={view === "new" ? `Unhandled alerts (${rows.length})` : `Handled alerts (${rows.length})`}>
            <thead>
              <tr>
                <th scope="col">Alert</th>
                <th scope="col">Waiting</th>
                <th scope="col">Channel</th>
                <th scope="col">Caller</th>
                <th scope="col">Type given</th>
                <th scope="col">Location</th>
                <th scope="col">Flags</th>
                <th scope="col">Duplicates</th>
                <th scope="col">Handler</th>
                <th scope="col">
                  <span className="lcc-sr-only">Open</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((a) => (
                <tr key={a.alert_id}>
                  <th scope="row" className="fer-nowrap">
                    {a.reference}
                    {view === "handled" && (
                      <>
                        {" "}
                        <StatusBadge status={statusOf(ALERT_STATUS, a.status)} />
                      </>
                    )}
                  </th>
                  <td className="fer-num" role="timer" aria-live="off">
                    {view === "new" ? formatSeconds(secondsBetween(a.received_at, clockNow)) : formatDateTime(a.received_at)}
                  </td>
                  <td>
                    <StatusBadge status={statusOf(CHANNEL, a.channel)} />
                  </td>
                  <td>
                    {a.caller.redacted_at ? (
                      <span className="fer-muted">Removed</span>
                    ) : (
                      <>
                        {a.caller.display_name ?? "No name"}
                        {a.caller.phone ? `, ${a.caller.phone}` : ""}
                      </>
                    )}
                  </td>
                  <td>{a.incident_type ? names.incidentType(a.incident_type) : "—"}</td>
                  <td>{a.location ? `${a.location.plus_code} (${a.location.accuracy_class})` : <span className="fer-muted">Not yet</span>}</td>
                  <td>
                    <FlagBadges map={ALERT_FLAG} flags={a.flags} />
                  </td>
                  <td className="fer-num">{a.duplicate_candidate_count}</td>
                  <td>{a.handler_id ? personLabel(a.handler_id, principal.partyId, a.handler_name) : "—"}</td>
                  <td>
                    <Button variant="secondary" size="sm" onClick={() => setOpen(a.alert_id)} aria-label={`Open alert ${a.reference}`}>
                      Open
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Tabs>
      <p className="fer-hint">Callers are masked in this list; opening an alert shows the caller in full (recorded).</p>
      {can(principal, "viewRefusedSos") && <RefusedSosPanel />}
      {creating && (
        <NewAlertDialog
          onClose={() => setCreating(false)}
          onCreated={(a) => {
            setCreating(false);
            setOpen(a.alert_id);
          }}
        />
      )}
      {!can(principal, "createAlert") && can(principal, "viewAlerts") && <Notice>The chief reads the queue; call-takers and dispatchers record and handle alerts.</Notice>}
    </div>
  );
}

